import CommandStatusBadge from '@/components/client/command-status-badge';
import CopyablePhone from '@/components/copyable-phone';
import { getCommandTypeInfo } from '@/components/client/command-type-info';
import type { IClientAction } from '@/entities/client.entity';
import { cn } from '@/lib/utils';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';

const formatter = buildFormatter(russianStrings);

function getStatusAccent(action: IClientAction) {
	if (action.status === 2) return 'border-l-chart-2';
	if (action.status === 3) return 'border-l-destructive';
	if (action.status === 1) return 'border-l-chart-5';
	return 'border-l-muted-foreground/40';
}

interface CommandCardProps {
	action: IClientAction;
	onCopy?: (text: string) => void;
	className?: string;
}

export default function CommandCard({ action, onCopy, className }: CommandCardProps) {
	const typeInfo = getCommandTypeInfo(action);
	const TypeIcon = typeInfo.icon;
	const statusAccent = getStatusAccent(action);

	return (
		<div className={cn('rounded-lg border border-border bg-card p-2.5 shadow-sm border-l-[3px]', statusAccent, className)}>
			<div className='flex items-start gap-2'>
				<div className={cn('flex size-7 shrink-0 items-center justify-center rounded-lg', typeInfo.accent)}>
					<TypeIcon size={14} />
				</div>

				<div className='min-w-0 flex-1'>
					<div className='flex items-start justify-between gap-2'>
						<div className='min-w-0'>
							<p className='text-sm font-medium'>{typeInfo.label}</p>
							{action.params?.subId ? <p className='text-xs text-muted-foreground'>SIM {action.params.subId}</p> : null}
						</div>
						<CommandStatusBadge action={action} />
					</div>

					{action.params?.phone ? (
						<CopyablePhone
							value={action.params.phone}
							onCopy={text => onCopy?.(text)}
							className='mt-1 block text-sm text-chart-3 rounded-xs px-0.5 truncate max-w-full'
						/>
					) : null}

					{action.params?.text ? (
						<button
							type='button'
							className='mt-1 block text-left text-sm text-muted-foreground hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate max-w-full'
							onClick={() => onCopy?.(action.params!.text!)}
						>
							{action.params.text}
						</button>
					) : null}

					<div className='mt-1.5 text-xs text-muted-foreground'>
						<TimeAgo date={action.createdAt} formatter={formatter} />
					</div>
				</div>
			</div>
		</div>
	);
}
